import React, { useEffect, useState } from "react";
import { Box, Button, TextField, MenuItem, IconButton } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import axios from "axios";
import { useFormik } from "formik";
import * as yup from "yup";
import Header from "../../AdminComponents/Header";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ViewCategories = ({ id, handleFormClose, refreshUserList }) => {
  const [categoryDetails, setCategoryDetails] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch category details
  useEffect(() => {
    const fetchCategoryDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${BASE_URL}/api/v1/VendorManagement/show-Categories/${id}`
        );
        if (response.data.data?.ShowCategorie) {
          const categoryData = response.data.data.ShowCategorie;

          // Parse CategorieFields JSON
          const parsedFields = Array.isArray(
            JSON.parse(categoryData.CategorieFields)
          )
            ? categoryData.CategorieFields
            : categoryData.CategorieFields
            ? JSON.parse(JSON.parse(categoryData.CategorieFields))
            : [];

          setCategoryDetails({
            ...categoryData,
            CategorieFields: parsedFields,
          });

          setFields(Array.isArray(parsedFields) ? parsedFields : []);
        } else {
          setError("Failed to fetch category details.");
        }
      } catch (err) {
        console.error("Error fetching category details:", err);
        setError("An error occurred while fetching category details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCategoryDetails();
    }
  }, [id]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Prepare FormData
      const formData = new FormData();
      formData.append("CategorieName", values.CategorieName);

      // Handle the CategorieImage
      if (values.CategorieImage) {
        // Append the new image file if uploaded
        formData.append("CategorieImage", values.CategorieImage);
      } else if (categoryDetails?.CategorieImage) {
        // Append the existing image for the particular category
        const existingImageFilename = JSON.parse(
          categoryDetails.CategorieImage
        )?.[0]?.filename;
        if (existingImageFilename) {
          // Fetch the existing image as a Blob
          const imageUrl = `${BASE_URL}/uploads/${existingImageFilename}`;
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          formData.append("CategorieImage", blob, existingImageFilename); // Use the existing filename
        }
      }

      // Maintain the order of fields and add them to FormData
      formData.append("CategorieFields", JSON.stringify(fields));

      // Send PUT request
      const response = await axios.put(
        `${BASE_URL}/api/v1/VendorManagement/edit-Categories/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data?.message === "Categories updated successfully") {
        console.log("Category updated successfully:", response.data);
        refreshUserList();
        handleFormClose();
      } else {
        setError("Failed to update category. Please check the response.");
      }
    } catch (err) {
      console.error("Error updating category:", err);
      setError("An error occurred while updating the category.");
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = yup.object({
    CategorieName: yup.string().required("Category Name is required"),
    CategorieImage: yup.mixed().nullable().notRequired(), // Make the image optional
  });

  const formik = useFormik({
    initialValues: {
      CategorieName: categoryDetails?.CategorieName || "",
      CategorieImage: null,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    console.log("Selected file:", file); // Check if the file is correctly captured
    formik.setFieldValue("CategorieImage", file);
  };

  const moveField = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= fields.length) return; // Prevent out-of-bounds movement

    const updatedFields = [...fields];
    // Swap the fields
    [updatedFields[fromIndex], updatedFields[toIndex]] = [
      updatedFields[toIndex],
      updatedFields[fromIndex],
    ];
    setFields(updatedFields);
  };

  const addField = () => {
    setFields([
      ...fields,
      { fieldName: "", fieldLabel: "", fieldType: "", options: [] },
    ]);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const updateField = (index, property, value) => {
    const updatedFields = fields.map((field, i) =>
      i === index ? { ...field, [property]: value } : field
    );
    setFields(updatedFields);
  };

  const updateOptions = (index, options) => {
    const updatedFields = fields?.map((field, i) =>
      i === index ? { ...field, options: options.split(",") } : field
    );
    setFields(updatedFields);
  };

  return (
    <Box m="20px">
    <Header title="View Category" subtitle="View and Edit Category Details" />
  
    {/* Back Button */}
    <Button
      variant="contained"
      color="secondary"
      onClick={handleFormClose}
      sx={{ position: "absolute", top: "20px", left: "20px" }}
    >
      Back
    </Button>
  
    {loading ? (
      <p>Loading...</p>
    ) : error ? (
      <p>{error}</p>
    ) : (
      <form autoComplete="off" onSubmit={formik.handleSubmit}>
        
        {/* Main Flexbox Container */}
        <Box display="flex" gap="30px">
          
          {/* Left - Category Name & Image Upload */}
          <Box display="flex" flexDirection="column" flex="1" gap="20px">
            
            {/* Category Name Field */}
            <TextField
              fullWidth
              variant="filled"
              type="text"
              label="Category Name"
              name="CategorieName"
              value={formik.values.CategorieName}
              onChange={formik.handleChange}
              error={formik.touched.CategorieName && Boolean(formik.errors.CategorieName)}
              helperText={formik.touched.CategorieName && formik.errors.CategorieName}
              InputLabelProps={{ style: { fontSize: 17 } }}
              inputProps={{ style: { fontSize: 20 } }}
            />
  
            {/* Image Upload Field */}
            <TextField
              fullWidth
              variant="filled"
              type="file"
              label="Upload Image"
              InputLabelProps={{ shrink: true, style: { fontSize: 17 } }}
              inputProps={{ accept: "image/*", style: { fontSize: 20 } }}
              onChange={handleImageChange}
            />
          </Box>
  
          {/* Right - Image Preview */}
          {categoryDetails?.CategorieImage &&
            (() => {
              try {
                const parsedImage = JSON.parse(categoryDetails.CategorieImage);
                const imageUrl = `${BASE_URL}/uploads/${parsedImage?.[0]?.filename || ""}`;
  
                return (
                  <Box flex="1" display="flex" justifyContent="center" alignItems="center">
                    <img
                      src={imageUrl}
                      alt="Category Preview"
                      style={{ width: "200px", height: "auto", borderRadius: "10px" }}
                    />
                  </Box>
                );
              } catch (error) {
                console.error("Error parsing CategorieImage:", error);
                return null;
              }
            })()}
        </Box>
  
        {/* Dynamic Fields Section */}
        <Box mt="20px">
          {fields.map((field, index) => (
            <Box key={index} display="flex" gap="20px" mb="20px" alignItems="center">
              
              {/* Field Name */}
              <TextField
                label="Field Name (Key)"
                value={field.fieldName}
                onChange={(e) => updateField(index, "fieldName", e.target.value)}
                variant="filled"
                sx={{ flex: 1 }}
              />
  
              {/* Field Label */}
              <TextField
                label="Field Label (Display)"
                value={field.fieldLabel}
                onChange={(e) => updateField(index, "fieldLabel", e.target.value)}
                variant="filled"
                sx={{ flex: 1 }}
              />
  
              {/* Field Type */}
              <TextField
                select
                label="Field Type"
                value={field.fieldType}
                onChange={(e) => updateField(index, "fieldType", e.target.value)}
                variant="filled"
                sx={{ flex: 1 }}
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="select">Select</MenuItem>
                <MenuItem value="multiselect">Multiselect</MenuItem>
              </TextField>
  
              {/* Options Field (Only for select/multiselect) */}
              {(field.fieldType === "select" || field.fieldType === "multiselect") && (
                <TextField
                  label="Options (comma-separated)"
                  value={field.options.join(",")}
                  onChange={(e) => updateOptions(index, e.target.value)}
                  variant="filled"
                  sx={{ flex: 1 }}
                />
              )}
  
              {/* Delete Button (Only for fields after first two) */}
              {index >= 2 && (
                <IconButton onClick={() => removeField(index)} color="secondary">
                  <Remove />
                </IconButton>
              )}
  
              {/* Move Up/Down Buttons */}
              <Box display="flex">
                <IconButton disabled={index === 0} onClick={() => moveField(index, index - 1)}>
                  ↑
                </IconButton>
                <IconButton disabled={index === fields.length - 1} onClick={() => moveField(index, index + 1)}>
                  ↓
                </IconButton>
              </Box>
  
            </Box>
          ))}
        </Box>
  
        {/* Add New Field Button */}
        <Box display="flex" justifyContent="center" mt="20px">
          <Button variant="outlined" onClick={addField} startIcon={<Add />}>
            Add New Field
          </Button>
        </Box>
  
        {/* Submit Button */}
        <Box display="flex" justifyContent="center" mt="20px">
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </Box>
  
      </form>
    )}
  </Box>
  
  );
};

export default ViewCategories;
